/**
 * Admin Build Section - Carrier Onboarding with Package Selection
 * Flow: Select Package (Foundation/Accelerator/Elite) → View Package-Specific Metrics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import ClientProfileModal from '../../../components/admin/ClientProfileModal';
import { 
  FileText, Clock, CheckCircle, Users, TrendingUp, Zap,
  AlertTriangle, Building, ClipboardCheck, Loader2, RefreshCw, 
  Eye, DollarSign, Rocket, FileCheck, Star,
  ArrowRight, Package, Shield, ArrowLeft, Crown
} from 'lucide-react';

const AdminBuildSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [activeTab, setActiveTab] = useState('pipeline');
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Client profiles database (shared across admin sections)
  const clientProfiles = {
    'Solo Express LLC': {
      id: 'CLT-001',
      company: 'Solo Express LLC',
      package: 'Foundation',
      contact: { name: 'James Wilson', title: 'Owner/Operator', email: 'james@soloexpress.com', phone: '(214) 555-0123' },
      address: '1234 Highway Dr, Dallas, TX 75201',
      dotNumber: '3847291',
      mcNumber: '1029384',
      fleet: { trucks: 1, trailers: 1, drivers: 1 },
      insurance: { expiry: '2026-08-15', provider: 'Progressive Commercial', coverage: '$1,000,000' },
      joined: '2025-11-15',
      stats: { totalLoads: 89, onTimeDelivery: 94, avgRating: 4.7, totalRevenue: 187500, avgRatePerMile: 2.45 },
      loadHistory: [
        { id: 'BRZ-001', route: 'Dallas, TX → Atlanta, GA', date: '2026-03-18', rate: 3200, status: 'in_transit' },
        { id: 'BRZ-012', route: 'Houston, TX → Memphis, TN', date: '2026-03-10', rate: 1850, status: 'delivered' }
      ],
      notes: 'Reliable owner-operator. Prefers regional Southeast lanes. Currently in Build Phase 1.'
    },
    'Highway Runner': {
      id: 'CLT-002',
      company: 'Highway Runner',
      package: 'Foundation',
      contact: { name: 'Mike Chen', title: 'Owner', email: 'mike@highwayrunner.com', phone: '(713) 555-0456' },
      address: '567 Transport Blvd, Houston, TX 77001',
      dotNumber: '3921847',
      mcNumber: '1183726',
      fleet: { trucks: 1, trailers: 2, drivers: 1 },
      insurance: { expiry: '2026-06-20', provider: 'Northland Insurance', coverage: '$1,000,000' },
      joined: '2025-12-01',
      stats: { totalLoads: 67, onTimeDelivery: 91, avgRating: 4.5, totalRevenue: 142000, avgRatePerMile: 2.38 },
      loadHistory: [
        { id: 'BRZ-002', route: 'Houston, TX → Phoenix, AZ', date: '2026-03-21', rate: 2850, status: 'pending' }
      ],
      notes: 'Good for long-haul Southwest routes. In Build Formation phase.'
    },
    'Mile High Trucking': {
      id: 'CLT-003',
      company: 'Mile High Trucking',
      package: 'Foundation',
      contact: { name: 'Sarah Johnson', title: 'Owner/Operator', email: 'sarah@milehigh.com', phone: '(303) 555-0789' },
      address: '890 Mountain Ave, Denver, CO 80201',
      dotNumber: '4019283',
      mcNumber: '1247583',
      fleet: { trucks: 1, trailers: 1, drivers: 1 },
      insurance: { expiry: '2026-09-10', provider: 'Great West Casualty', coverage: '$1,000,000' },
      joined: '2026-01-10',
      stats: { totalLoads: 45, onTimeDelivery: 98, avgRating: 4.9, totalRevenue: 98500, avgRatePerMile: 2.52 },
      loadHistory: [
        { id: 'BRZ-003', route: 'Los Angeles, CA → Denver, CO', date: '2026-03-15', rate: 2400, status: 'delivered' }
      ],
      notes: 'Excellent reliability. Ready for launch activation.'
    },
    'Eagle Transport Inc': {
      id: 'CLT-004',
      company: 'Eagle Transport Inc',
      package: 'Accelerator',
      contact: { name: 'Robert Martinez', title: 'Fleet Manager', email: 'robert@eagletransport.com', phone: '(206) 555-1234' },
      address: '2345 Pacific Way, Seattle, WA 98101',
      dotNumber: '2847291',
      mcNumber: '982734',
      fleet: { trucks: 3, trailers: 4, drivers: 4 },
      insurance: { expiry: '2026-07-25', provider: 'Sentry Insurance', coverage: '$2,000,000' },
      joined: '2025-08-20',
      stats: { totalLoads: 234, onTimeDelivery: 96, avgRating: 4.8, totalRevenue: 567000, avgRatePerMile: 2.78 },
      loadHistory: [
        { id: 'SLV-001', route: 'Seattle, WA → Salt Lake City, UT', date: '2026-03-17', rate: 2950, status: 'in_transit' }
      ],
      notes: 'Growing carrier with excellent track record. In compliance handoff phase.'
    },
    'Prime Freight Co': {
      id: 'CLT-015',
      company: 'Prime Freight Co',
      package: 'Accelerator',
      contact: { name: 'David Lee', title: 'Owner', email: 'david@primefreight.com', phone: '(404) 555-3456' },
      address: '456 Freight Rd, Atlanta, GA 30301',
      dotNumber: '3829174',
      mcNumber: '1093847',
      fleet: { trucks: 2, trailers: 2, drivers: 2 },
      insurance: { expiry: '2026-10-15', provider: 'Progressive Commercial', coverage: '$1,500,000' },
      joined: '2025-10-01',
      stats: { totalLoads: 112, onTimeDelivery: 93, avgRating: 4.6, totalRevenue: 245000, avgRatePerMile: 2.62 },
      loadHistory: [
        { id: 'ACC-001', route: 'Atlanta, GA → Nashville, TN', date: '2026-03-19', rate: 1800, status: 'in_transit' }
      ],
      notes: 'Solid carrier, working through authority filing phase.'
    },
    'Swift Carriers': {
      id: 'CLT-005',
      company: 'Swift Carriers',
      package: 'Accelerator',
      contact: { name: 'Amanda Lee', title: 'Operations Manager', email: 'amanda@swiftcarriers.com', phone: '(503) 555-5678' },
      address: '789 Commerce St, Portland, OR 97201',
      dotNumber: '3182736',
      mcNumber: '1047382',
      fleet: { trucks: 2, trailers: 3, drivers: 3 },
      insurance: { expiry: '2026-11-30', provider: 'Progressive Commercial', coverage: '$1,500,000' },
      joined: '2025-09-15',
      stats: { totalLoads: 178, onTimeDelivery: 93, avgRating: 4.6, totalRevenue: 423000, avgRatePerMile: 2.65 },
      loadHistory: [
        { id: 'SLV-002', route: 'Portland, OR → San Francisco, CA', date: '2026-03-18', rate: 1850, status: 'in_transit' }
      ],
      notes: 'Reliable carrier for Pacific Northwest routes. In formation phase.'
    },
    'Atlas Logistics': {
      id: 'CLT-006',
      company: 'Atlas Logistics',
      package: 'Elite',
      contact: { name: 'Michael Rodriguez', title: 'CEO', email: 'michael@atlaslogistics.com', phone: '(310) 555-9012' },
      address: '1000 Logistics Pkwy, Los Angeles, CA 90001',
      dotNumber: '1928374',
      mcNumber: '847293',
      fleet: { trucks: 8, trailers: 12, drivers: 10 },
      insurance: { expiry: '2026-12-15', provider: 'Zurich Insurance', coverage: '$5,000,000' },
      joined: '2025-03-01',
      stats: { totalLoads: 892, onTimeDelivery: 98, avgRating: 4.9, totalRevenue: 2450000, avgRatePerMile: 3.12 },
      loadHistory: [
        { id: 'GLD-001', route: 'Los Angeles, CA → Chicago, IL', date: '2026-03-17', rate: 5800, status: 'in_transit' },
        { id: 'GLD-002', route: 'New York, NY → Miami, FL', date: '2026-03-18', rate: 4200, status: 'in_transit' }
      ],
      notes: 'Premium carrier. Dedicated account manager assigned. Near launch activation.'
    },
    'Titan Carriers': {
      id: 'CLT-007',
      company: 'Titan Carriers',
      package: 'Elite',
      contact: { name: 'Patricia Nguyen', title: 'VP Operations', email: 'patricia@titancarriers.com', phone: '(832) 555-3456' },
      address: '5678 Industrial Dr, Houston, TX 77002',
      dotNumber: '2019283',
      mcNumber: '912847',
      fleet: { trucks: 6, trailers: 8, drivers: 7 },
      insurance: { expiry: '2026-10-20', provider: 'Great West Casualty', coverage: '$3,000,000' },
      joined: '2025-05-10',
      stats: { totalLoads: 567, onTimeDelivery: 97, avgRating: 4.8, totalRevenue: 1780000, avgRatePerMile: 3.05 },
      loadHistory: [
        { id: 'GLD-003', route: 'Houston, TX → Los Angeles, CA', date: '2026-03-21', rate: 4650, status: 'pending' }
      ],
      notes: 'Excellent for industrial equipment. In compliance handoff phase.'
    },
    'Mountain Express': {
      id: 'CLT-008',
      company: 'Mountain Express',
      package: 'Elite',
      contact: { name: 'Emily Jackson', title: 'Owner', email: 'emily@mountainexpress.com', phone: '(720) 555-7890' },
      address: '3456 Rocky Mountain Blvd, Denver, CO 80202',
      dotNumber: '3847261',
      mcNumber: '1129384',
      fleet: { trucks: 5, trailers: 6, drivers: 6 },
      insurance: { expiry: '2026-08-05', provider: 'Northland Insurance', coverage: '$2,500,000' },
      joined: '2025-06-20',
      stats: { totalLoads: 423, onTimeDelivery: 95, avgRating: 4.7, totalRevenue: 1250000, avgRatePerMile: 2.95 },
      loadHistory: [
        { id: 'GLD-005', route: 'Denver, CO → Kansas City, MO', date: '2026-03-19', rate: 1680, status: 'in_transit' }
      ],
      notes: 'Specializes in mountain region routes. Working through authority phase.'
    }
  };

  const handleClientClick = (clientName) => {
    const profile = clientProfiles[clientName];
    if (profile) {
      setSelectedClient(profile);
    }
  };
  
  // Package definitions for Build (Carrier Onboarding)
  const packages = {
    foundation: {
      name: 'Foundation',
      subtitle: 'Essential Start',
      price: '$997',
      priceType: ' one-time',
      color: '#6B7280',
      icon: FileText,
      description: 'Basic carrier setup for new owner-operators',
      features: [
        'LLC formation guidance',
        'EIN registration assistance',
        'DOT number filing guidance',
        'MC authority filing guidance',
        'BOC-3 coordination',
        'UCR guidance',
        'IRP & IFTA checklist',
        '30 Days Compliance Support',
        '30 Days Dispatch Support',
        'Email support'
      ],
      stats: {
        activeClients: 18,
        completed: 42,
        avgCompletionDays: 12,
        totalRevenue: 17946,
        conversionRate: 72,
        satisfaction: 4.6
      },
      clients: [
        { name: 'Solo Express LLC', stage: 'authority', progress: 65, daysInStage: 5 },
        { name: 'Highway Runner', stage: 'formation', progress: 35, daysInStage: 3 },
        { name: 'Mile High Trucking', stage: 'launch_activation', progress: 95, daysInStage: 1 }
      ]
    },
    accelerator: {
      name: 'Accelerator',
      subtitle: 'Authority + Compliance',
      price: '$1,997',
      priceType: ' one-time',
      color: '#3B82F6',
      icon: Zap,
      description: 'Enhanced setup with compliance support',
      features: [
        'Everything in Foundation',
        'Drug & Alcohol Consortium setup',
        'Clearinghouse registration guidance',
        'Compliance calendar setup',
        '60 Days Compliance Monitoring',
        'Carrier packet template',
        '60 Days Dispatch Support',
        'Priority email support'
      ],
      stats: {
        activeClients: 19,
        completed: 38,
        avgCompletionDays: 14,
        totalRevenue: 37943,
        conversionRate: 68,
        satisfaction: 4.8
      },
      clients: [
        { name: 'Eagle Transport Inc', stage: 'compliance_handoff', progress: 80, daysInStage: 3 },
        { name: 'Prime Freight Co', stage: 'authority', progress: 55, daysInStage: 7 },
        { name: 'Swift Carriers', stage: 'formation', progress: 30, daysInStage: 4 }
      ]
    },
    elite: {
      name: 'Elite',
      subtitle: 'Full Business Launch',
      price: '$2,997',
      priceType: ' one-time',
      color: '#D4AF37',
      icon: Crown,
      popular: true,
      description: 'Complete business setup with dedicated support',
      features: [
        'Everything in Accelerator',
        'Business bank account setup guidance',
        'Factoring company assistance',
        'Fuel card setup guidance',
        'ELD consultation',
        '90 Days Compliance Support',
        'Business growth blueprint',
        '90 Days Dispatch Support',
        'Dedicated account manager'
      ],
      stats: {
        activeClients: 10,
        completed: 24,
        avgCompletionDays: 16,
        totalRevenue: 29970,
        conversionRate: 85,
        satisfaction: 4.9
      },
      clients: [
        { name: 'Atlas Logistics', stage: 'launch_activation', progress: 90, daysInStage: 2 },
        { name: 'Titan Carriers', stage: 'compliance_handoff', progress: 75, daysInStage: 3 },
        { name: 'Mountain Express', stage: 'authority', progress: 60, daysInStage: 8 }
      ]
    }
  };

  const stageLabels = {
    intake: { label: 'Intake', color: '#F59E0B' },
    formation: { label: 'Formation', color: '#3B82F6' },
    authority: { label: 'Authority', color: '#8B5CF6' },
    compliance_handoff: { label: 'Compliance Handoff', color: '#10B981' },
    launch_activation: { label: 'Launch Activation', color: '#EC4899' }
  };

  // Overall stats
  const overallStats = {
    totalActiveClients: 47,
    totalCompleted: 104,
    avgCompletionDays: 14,
    totalRevenue: 85859
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTimeout(() => setLoading(false), 500);
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout title="Build" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  // Client Profile View
  if (selectedClient) {
    return (
      <AdminLayout title="Client Profile" subtitle={selectedClient.company}>
        <button
          onClick={() => setSelectedClient(null)}
          className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
          data-testid="build-back-from-profile"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Pipeline
        </button>
        <ClientProfileModal client={selectedClient} onClose={() => setSelectedClient(null)} isModal={false} />
      </AdminLayout>
    );
  }

  // Package Selection Screen
  if (!selectedPackage) {
    return (
      <AdminLayout 
        title="Build Management" 
        subtitle="Carrier Onboarding - Select a package to view pipeline"
      >
        {/* Overall Summary */}
        <div className="bg-gradient-to-r from-[#8B5CF6]/20 to-[#8B5CF6]/5 border border-[#8B5CF6]/30 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-lg font-bold mb-1">Onboarding Pipeline</h2>
              <p className="text-white/50 text-sm">{overallStats.totalActiveClients} active clients across all packages</p>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#10B981]">{overallStats.totalCompleted}</p>
                <p className="text-white/40 text-xs">Completed</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{overallStats.avgCompletionDays} days</p>
                <p className="text-white/40 text-xs">Avg Completion</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#D4AF37]">${(overallStats.totalRevenue / 1000).toFixed(1)}k</p>
                <p className="text-white/40 text-xs">Total Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Package Selection Cards */}
        <h3 className="text-white font-bold mb-4">Select a Package to View Pipeline</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(packages).map(([key, pkg]) => (
            <div
              key={key}
              onClick={() => setSelectedPackage(key)}
              className={`bg-[#121212] border p-6 cursor-pointer hover:bg-[#1a1a1a] transition-all relative ${
                pkg.popular ? 'border-[#D4AF37]' : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={`build-package-${key}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#D4AF37] text-black text-xs font-bold px-3 py-1">MOST POPULAR</span>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded" style={{ backgroundColor: `${pkg.color}20` }}>
                  <pkg.icon className="w-6 h-6" style={{ color: pkg.color }} />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider">{pkg.name}</p>
                  <p className="text-white font-bold">{pkg.subtitle}</p>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-3xl font-bold" style={{ color: pkg.color }}>{pkg.price}</span>
                <span className="text-white/40 text-sm">{pkg.priceType}</span>
              </div>

              <p className="text-white/50 text-sm mb-4">{pkg.description}</p>

              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-white/40 text-xs">Active Clients</p>
                    <p className="text-white font-bold text-xl">{pkg.stats.activeClients}</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Completed</p>
                    <p className="text-[#10B981] font-bold text-xl">{pkg.stats.completed}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Revenue</span>
                  <span className="text-[#D4AF37] font-bold">${pkg.stats.totalRevenue.toLocaleString()}</span>
                </div>
              </div>

              <button 
                className="w-full mt-4 py-3 text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: `${pkg.color}20`, 
                  color: pkg.color,
                  border: `1px solid ${pkg.color}40`
                }}
              >
                View {pkg.name} Pipeline →
              </button>
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  // Package-Specific Pipeline Screen
  const pkg = packages[selectedPackage];

  return (
    <AdminLayout 
      title={`${pkg.name} Package`} 
      subtitle={pkg.subtitle}
    >
      {/* Back Button */}
      <button
        onClick={() => setSelectedPackage(null)}
        className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
        data-testid="build-back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to All Packages
      </button>

      {/* Package Header */}
      <div className="border p-4 sm:p-6 mb-6" style={{ 
        backgroundColor: `${pkg.color}10`, 
        borderColor: `${pkg.color}40` 
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded" style={{ backgroundColor: `${pkg.color}20` }}>
              <pkg.icon className="w-8 h-8" style={{ color: pkg.color }} />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">{pkg.name} - {pkg.subtitle}</h2>
              <p className="text-white/50">{pkg.price}{pkg.priceType} • {pkg.stats.activeClients} active clients</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: pkg.color }}>${pkg.stats.totalRevenue.toLocaleString()}</p>
              <p className="text-white/40 text-xs">Total Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <StatCard icon={Users} label="Active Clients" value={pkg.stats.activeClients} color={pkg.color} onClick={() => setActiveTab('pipeline')} />
        <StatCard icon={CheckCircle} label="Completed" value={pkg.stats.completed} color="#10B981" onClick={() => setActiveTab('completed')} />
        <StatCard icon={Clock} label="Avg Completion" value={`${pkg.stats.avgCompletionDays} days`} color="#3B82F6" onClick={() => setActiveTab('pipeline')} />
        <StatCard icon={DollarSign} label="Revenue" value={`$${pkg.stats.totalRevenue.toLocaleString()}`} color="#22C55E" onClick={() => setActiveTab('overview')} />
        <StatCard icon={TrendingUp} label="Conversion Rate" value={`${pkg.stats.conversionRate}%`} color="#8B5CF6" onClick={() => setActiveTab('overview')} />
        <StatCard icon={Star} label="Satisfaction" value={pkg.stats.satisfaction} color="#D4AF37" onClick={() => setActiveTab('overview')} />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'pipeline', label: 'Active Pipeline', icon: Clock },
          { id: 'completed', label: 'Completed', icon: CheckCircle },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`build-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[#8B5CF6]/10 border border-[#8B5CF6]/30'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
            style={{ color: activeTab === tab.id ? pkg.color : undefined }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Package Features */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <h3 className="text-white font-bold mb-4">Package Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pkg.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${pkg.color}20` }}>
                    <CheckCircle className="w-3 h-3" style={{ color: pkg.color }} />
                  </div>
                  <span className="text-white/70 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-white">{pkg.stats.activeClients}</p>
              <p className="text-white/50 text-xs">Active Clients</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#10B981]">{pkg.stats.completed}</p>
              <p className="text-white/50 text-xs">Completed</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#22C55E]">${pkg.stats.totalRevenue.toLocaleString()}</p>
              <p className="text-white/50 text-xs">Revenue</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#D4AF37]">{pkg.stats.satisfaction}★</p>
              <p className="text-white/50 text-xs">Satisfaction</p>
            </div>
          </div>
        </div>
      )}

      {/* Pipeline Tab */}
      {activeTab === 'pipeline' && (
        <div className="bg-[#121212] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-white font-bold">Active {pkg.name} Clients</h3>
            <span className="text-white/50 text-sm">{pkg.stats.activeClients} in pipeline</span>
          </div>
          <div className="divide-y divide-white/5">
            {pkg.clients.map((client, idx) => {
              const stage = stageLabels[client.stage];
              return (
                <div key={idx} className="p-4 hover:bg-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <button 
                        onClick={() => handleClientClick(client.name)}
                        className="text-white font-medium hover:text-[#D4AF37] hover:underline transition-colors text-left"
                        data-testid={`build-client-${idx}`}
                      >
                        {client.name}
                      </button>
                      <p className="text-white/40 text-xs">{client.daysInStage} days in stage</p>
                    </div>
                    <span className="px-2 py-1 text-xs" style={{ backgroundColor: `${stage.color}20`, color: stage.color }}>
                      {stage.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="h-2 bg-white/10 overflow-hidden">
                        <div 
                          className="h-full transition-all"
                          style={{ width: `${client.progress}%`, backgroundColor: pkg.color }}
                        />
                      </div>
                    </div>
                    <span className="text-white/50 text-sm">{client.progress}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed Tab */}
      {activeTab === 'completed' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
              <p className="text-4xl font-bold text-white">{pkg.stats.completed}</p>
              <p className="text-white/50">Total Completed</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Clock className="w-12 h-12 text-[#3B82F6] mx-auto mb-3" />
              <p className="text-4xl font-bold text-white">{pkg.stats.avgCompletionDays} days</p>
              <p className="text-white/50">Avg Completion Time</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Star className="w-12 h-12 text-[#D4AF37] mx-auto mb-3" />
              <p className="text-4xl font-bold text-white">{pkg.stats.satisfaction}</p>
              <p className="text-white/50">Avg Satisfaction</p>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-[#121212] border border-white/10 p-4 sm:p-5 cursor-pointer hover:bg-[#1a1a1a] hover:border-white/20 transition-all"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-white/50 text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

export default AdminBuildSection;
